/**
 * NotificationsPage Component
 * Full notifications history with filtering and bulk actions
 */

import { useState, useEffect } from 'react'
import NotificationItem from '../components/Notifications/NotificationItem'
import {
  listNotifications,
  markNotificationAsRead,
  deleteNotification,
  markAllAsRead,
  deleteAllRead,
  type Notification,
  type ListNotificationsParams,
} from '../services/notifications.service'

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [readFilter, setReadFilter] = useState<'all' | 'true' | 'false'>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  // Pagination
  const [total, setTotal] = useState(0)
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)

  // Load notifications
  useEffect(() => {
    loadNotifications()
  }, [readFilter, categoryFilter, offset])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: ListNotificationsParams = {
        is_read: readFilter,
        limit,
        offset,
      }

      if (categoryFilter !== 'all') {
        params.category = categoryFilter as any
      }

      const response = await listNotifications(params)
      setNotifications(response.notifications)
      setTotal(response.pagination.total)
      setUnreadCount(response.unread_count)
    } catch (err) {
      console.error('Failed to load notifications:', err)
      setError('無法載入通知列表')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1, read_at: Date.now() } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Failed to mark as read:', err)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteNotification(id)
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      setTotal((prev) => prev - 1)
    } catch (err) {
      console.error('Failed to delete notification:', err)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const result = await markAllAsRead()
      if (result.success) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, is_read: 1, read_at: Date.now() }))
        )
        setUnreadCount(0)
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err)
    }
  }

  const handleDeleteAllRead = async () => {
    if (!confirm('確定要刪除所有已讀通知嗎?')) {
      return
    }

    try {
      const result = await deleteAllRead()
      if (result.success) {
        // Reload notifications after bulk delete
        loadNotifications()
      }
    } catch (err) {
      console.error('Failed to delete all read:', err)
    }
  }

  const handleReadFilterChange = (value: string) => {
    setReadFilter(value as 'all' | 'true' | 'false')
    setOffset(0) // Reset to first page
  }

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value)
    setOffset(0) // Reset to first page
  }

  const handlePageChange = (newOffset: number) => {
    setOffset(newOffset)
    window.scrollTo(0, 0)
  }

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(offset / limit) + 1
  const hasUnread = unreadCount > 0
  const hasRead = notifications.some((n) => n.is_read === 1)

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>通知中心</h1>
          {unreadCount > 0 && (
            <p style={styles.subtitle}>
              您有 <strong>{unreadCount}</strong> 則未讀通知
            </p>
          )}
        </div>
      </div>

      {/* Filters and bulk actions */}
      <div style={styles.controls}>
        <div style={styles.filters}>
          {/* Read status filter */}
          <select
            value={readFilter}
            onChange={(e) => handleReadFilterChange(e.target.value)}
            style={styles.select}
          >
            <option value="all">全部通知</option>
            <option value="false">未讀</option>
            <option value="true">已讀</option>
          </select>

          {/* Category filter */}
          <select
            value={categoryFilter}
            onChange={(e) => handleCategoryFilterChange(e.target.value)}
            style={styles.select}
          >
            <option value="all">所有類別</option>
            <option value="api_key">API Key</option>
            <option value="bot">Bot</option>
            <option value="system">系統</option>
            <option value="report">報表</option>
          </select>
        </div>

        {/* Bulk actions */}
        <div style={styles.bulkActions}>
          {hasUnread && (
            <button onClick={handleMarkAllAsRead} style={styles.bulkButton}>
              ✓ 全部標為已讀
            </button>
          )}
          {hasRead && (
            <button onClick={handleDeleteAllRead} style={styles.bulkButtonDanger}>
              🗑️ 刪除已讀
            </button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      <div style={styles.listContainer}>
        {loading && (
          <div style={styles.emptyState}>
            <p style={styles.loadingText}>載入中...</p>
          </div>
        )}

        {error && (
          <div style={styles.emptyState}>
            <p style={styles.errorText}>{error}</p>
            <button onClick={loadNotifications} style={styles.retryButton}>
              重試
            </button>
          </div>
        )}

        {!loading && !error && notifications.length === 0 && (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🔔</span>
            <p style={styles.emptyText}>沒有通知</p>
          </div>
        )}

        {!loading && !error && notifications.length > 0 && (
          <div style={styles.notificationsList}>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => handlePageChange(Math.max(0, offset - limit))}
            disabled={offset === 0}
            style={{
              ...styles.paginationButton,
              ...(offset === 0 ? styles.paginationButtonDisabled : {}),
            }}
          >
            ← 上一頁
          </button>

          <span style={styles.paginationInfo}>
            第 {currentPage} 頁 / 共 {totalPages} 頁 ({total} 則通知)
          </span>

          <button
            onClick={() => handlePageChange(offset + limit)}
            disabled={offset + limit >= total}
            style={{
              ...styles.paginationButton,
              ...(offset + limit >= total ? styles.paginationButtonDisabled : {}),
            }}
          >
            下一頁 →
          </button>
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  bulkActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  bulkButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#2563eb',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  bulkButtonDanger: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #fee2e2',
    backgroundColor: 'white',
    color: '#ef4444',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  listContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  notificationsList: {
    minHeight: '200px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  errorText: {
    fontSize: '16px',
    color: '#ef4444',
    margin: '0 0 16px 0',
  },
  retryButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '30px',
  },
  paginationButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#1f2937',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#6b7280',
  },
}
